import PerfectScrollbar from 'react-perfect-scrollbar';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toggleSidebar } from '../../store/themeConfigSlice';
import AnimateHeight from 'react-animate-height';
import { IRootState } from '../../store';
import { useState, useEffect } from 'react';
import IconCaretsDown from '../Icon/IconCaretsDown';
import IconCaretDown from '../Icon/IconCaretDown';
import IconMenuDashboard from '../Icon/Menu/IconMenuDashboard';
import IconMinus from '../Icon/IconMinus';
import { useUserContext } from '../../contexts/UserContext';
import { useLiveUserPlan } from '../../services/subscriptionServices';
import { selectSettings } from '../../store/authSlice';
import useSwipe from '../../hooks/useSwipe';
import { getAppMode } from '../../config/appMode';
import { SIDEBAR_CONFIG, NavEntry, NavGroup, NavLeaf, NavLinkItem, NavVisibilityContext, isEntryVisible, visibleChildrenOf } from '../../config/navigation';

const Sidebar = () => {
    const { userId, user } = useUserContext();
    useLiveUserPlan(userId);
    const [currentMenu, setCurrentMenu] = useState<string>('');
    const themeConfig = useSelector((state: IRootState) => state.themeConfig);
    const semidark = useSelector((state: IRootState) => state.themeConfig.semidark);
    const location = useLocation();
    const dispatch = useDispatch();
    const settings = useSelector(selectSettings);
    const preferences = settings?.preferences;

    const hideSidebarByDefault = preferences && preferences?.hideSidebarByDefault !== undefined ? preferences.hideSidebarByDefault : false;
    const isBasicMode = getAppMode(settings) === 'basic';

    const { t } = useTranslation();
    const toggleMenu = (value: string) => {
        setCurrentMenu((oldValue) => {
            return oldValue === value ? '' : value;
        });
    };

    useEffect(() => {
        const selector = document.querySelector('.sidebar ul a[href="' + window.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
            const ul: any = selector.closest('ul.sub-menu');
            if (ul) {
                let ele: any = ul.closest('li.menu').querySelectorAll('.nav-link') || [];
                if (ele.length) {
                    ele = ele[0];
                    setTimeout(() => {
                        ele.click();
                    });
                }
            }
        }
    }, []);

    useEffect(() => {
        if (window.innerWidth < 1024 && themeConfig.sidebar) {
            dispatch(toggleSidebar(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, hideSidebarByDefault]);

    // Swipe to close sidebar on mobile
    const swipeHandlers = useSwipe({
        onSwipedRight: () => {
            if (window.innerWidth < 1024 && themeConfig.sidebar) {
                dispatch(toggleSidebar(false));
            }
        },
    });

    const navContext: NavVisibilityContext = { isBasicMode };
    const label = (entry: { labelKey: string; defaultLabel?: string }) =>
        entry.defaultLabel ? t(entry.labelKey as any, { defaultValue: entry.defaultLabel }) : t(entry.labelKey as any);

    const renderSection = (entry: NavEntry & { kind: 'section' }) => (
        <h2 key={`section-${entry.labelKey}`} className="py-3 px-7 flex items-center uppercase font-extrabold bg-white-light/30 dark:bg-dark dark:bg-opacity-[0.08] -mx-4 mb-1">
            <IconMinus className="w-4 h-5 flex-none hidden" />
            <span className="flex-1">{label(entry)}</span>
        </h2>
    );

    const renderLink = (entry: NavLinkItem) => {
        const Icon = entry.icon;
        if (entry.variant === 'promo') {
            return (
                <li className="nav-item" key={entry.path}>
                    <NavLink to={entry.path} className="group mt-1">
                        <div className="flex items-center p-2 rounded-lg bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 dark:hover:bg-primary/30 transition-all border border-primary/20">
                            <Icon className="text-primary dark:text-primary-light shrink-0" {...(entry.iconProps || {})} />
                            <span className="ltr:pl-3 rtl:pr-3 font-bold text-primary dark:text-primary-light">{label(entry)}</span>
                        </div>
                    </NavLink>
                </li>
            );
        }
        return (
            <li className="nav-item" key={entry.path}>
                <NavLink className="group" to={entry.path}>
                    <div className="flex items-center">
                        <Icon className="group-hover:!text-primary dark:group-hover:!text-primary-light shrink-0" {...(entry.iconProps || {})} />
                        <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">{label(entry)}</span>
                    </div>
                </NavLink>
            </li>
        );
    };

    const renderGroup = (entry: NavGroup, children: NavLeaf[]) => {
        const Icon = entry.icon;
        return (
            <li className="menu nav-item" key={entry.id}>
                <button type="button" className={`${currentMenu === entry.id ? 'active' : ''} nav-link group w-full min-h-[44px]`} onClick={() => toggleMenu(entry.id)}>
                    <div className="flex items-center">
                        <Icon className="group-hover:!text-primary dark:group-hover:!text-primary-light shrink-0" />
                        <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">{label(entry)}</span>
                    </div>

                    <div className={currentMenu !== entry.id ? 'rtl:rotate-90 -rotate-90' : ''}>
                        <IconCaretDown />
                    </div>
                </button>

                <AnimateHeight duration={300} height={currentMenu === entry.id ? 'auto' : 0}>
                    {entry.body ? (
                        <div className="relative">{entry.body()}</div>
                    ) : (
                        <ul className="sub-menu text-gray-500">
                            {children.map(child => (
                                <li key={child.path}>
                                    <NavLink to={child.path}>{label(child)}</NavLink>
                                </li>
                            ))}
                        </ul>
                    )}
                </AnimateHeight>
            </li>
        );
    };

    const renderEntry = (entry: NavEntry) => {
        if (!isEntryVisible(entry, navContext)) return null;
        if (entry.kind === 'group') return renderGroup(entry, visibleChildrenOf(entry, navContext));
        return entry.kind === 'section' ? renderSection(entry) : renderLink(entry);
    };

    return (
        <div className={semidark ? 'dark' : ''}>
            <nav
                className={`sidebar fixed min-h-screen h-full top-0 bottom-0 w-[260px] shadow-[5px_0_25px_0_rgba(94,92,154,0.1)] dark:shadow-[5px_0_25px_0_rgba(0,0,0,0.4)] z-50 transition-all duration-300 ${semidark ? 'text-white-dark' : ''
                    }`}
                onTouchStart={swipeHandlers.onTouchStart}
                onTouchMove={swipeHandlers.onTouchMove}
                onTouchEnd={swipeHandlers.onTouchEnd}
            >
                <div className="h-full bg-white dark:bg-[#0e1726] flex flex-col shadow-lg border-r border-[#e0e6ed] dark:border-[#1b2e4b]">
                    <div className="flex justify-between items-center px-4 py-3">
                        <NavLink to="/" className="main-logo">
                            <img src="/assets/images/logo-combination-green.png" alt="logo" className="pr-8 dark:hidden" onLoad={(e) => e.currentTarget.classList.remove('hidden')} />
                            <img
                                src="/assets/images/logo-combination-coral.png"
                                alt="logo"
                                className="pr-8 hidden dark:hidden"
                                onLoad={(e) => e.currentTarget.classList.replace('dark:hidden', 'dark:block')}
                            />
                        </NavLink>

                        <button
                            type="button"
                            className="collapse-icon w-8 h-8 rounded-full flex flex-shrink-0 items-center hover:bg-gray-500/10 dark:hover:bg-dark-light/10 dark:text-white-light transition duration-300 rtl:rotate-180"
                            onClick={() => dispatch(toggleSidebar(undefined))}
                        >
                            <IconCaretsDown className="m-auto rotate-90" />
                        </button>
                    </div>
                    <PerfectScrollbar className="flex-1 relative">
                        <ul className="relative font-semibold space-y-0.5 p-4 py-0">

                            {SIDEBAR_CONFIG.map(renderEntry)}

                            {user?.isAdmin && (
                                <>
                                    <h2 className="py-3 px-7 flex items-center uppercase font-extrabold bg-white-light/30 dark:bg-dark dark:bg-opacity-[0.08] -mx-4 mb-1">
                                        <IconMinus className="w-4 h-5 flex-none hidden" />
                                        <span>Admin Dashboard</span>
                                    </h2>
                                    <li className="menu nav-item">
                                        <button type="button" className={`${currentMenu === 'admin' ? 'active' : ''} nav-link group w-full min-h-[44px]`} onClick={() => toggleMenu('admin')}>
                                            <div className="flex items-center">
                                                <IconMenuDashboard className="group-hover:!text-primary dark:group-hover:!text-primary-light shrink-0" />
                                                <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">Admin</span>
                                            </div>

                                            <div className={currentMenu !== 'admin' ? 'rtl:rotate-90 -rotate-90' : ''}>
                                                <IconCaretDown />
                                            </div>
                                        </button>

                                        <AnimateHeight duration={300} height={currentMenu === 'admin' ? 'auto' : 0}>
                                            <ul className="sub-menu text-gray-500">
                                                <li>
                                                    <NavLink to="/dashboard/admin/overview">Overview</NavLink>
                                                </li>
                                                <li>
                                                    <NavLink to="/dashboard/admin/referrals">Referrals</NavLink>
                                                </li>
                                                <li>
                                                    <NavLink to="/dashboard/admin/users">Users</NavLink>
                                                </li>
                                                <li>
                                                    <NavLink to="/dashboard/admin/finance">Finance</NavLink>
                                                </li>
                                                <li>
                                                    <NavLink to="/dashboard/admin/settings">Settings</NavLink>
                                                </li>
                                                <li>
                                                    <NavLink to="/dashboard/admin/offers">Offers</NavLink>
                                                </li>
                                            </ul>
                                        </AnimateHeight>
                                    </li>
                                </>
                            )}
                        </ul>
                    </PerfectScrollbar>
                </div>
            </nav>
        </div>
    );
};

function UpgradeLayer({ loading, visible }: { loading?: boolean; visible?: boolean }) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    return (
        <div
            className={`absolute h-full w-full rounded-md top-0 left-0 backdrop-filter backdrop-blur-[2px] flex items-center justify-center transition-all ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
        >
            {loading ? (
                <span className="animate-spin-tw border-4 border-gray-500 border-l-transparent rounded-full w-10 h-10 inline-block align-middle m-auto"></span>
            ) : (
                <button className="btn btn-danger rounded-full shadow-none hover:shadow" onClick={() => navigate('/dashboard/users/subscription-plans')}>
                    {t('upgrade')}
                </button>
            )}
        </div>
    );
}


export default Sidebar;
